import { useState, useEffect } from "react";
import { Row, Space, Modal, Form, Col, Input, Switch, Select } from "antd";
import { toastError, toastSuccess } from "utils/toasters";
import { addCity, editCity, getGovernmentsForList } from "apis/Network";
import {
  arabicNameWithNoRestrictionValidation,
  englishNameWithNoRestriction,
  englishLettersExist,
  arabicLettersExist,
} from "utils/RegexValidations";

// components
import PrimaryButton from "modules/SubComponents/Buttons/PrimaryButton";
import SecondaryButton from "modules/SubComponents/Buttons/SecondaryButton";
import MapHolder from "./MapHolder";

const AddEditModal = ({
  open,
  onCancel,
  subHeader,
  primaryBtnName,
  mode,
  rowData,
  countries,
  onSubmit,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [governmentsList, setGovernmentsList] = useState(null);
  const [submittable, setSubmittable] = useState(false);
  const [shapeCoords, setShapeCoords] = useState(null);
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const initialValues = {
    nameAr: "",
    nameEn: "",
    governmentId: undefined,
    isActive: true,
    isBusy: false,
    countryId: null,
  };

  const handleCoordsUpdate = (values) => {
    setShapeCoords(values);
  };

  const handleOnFinish = (values) => {
    if (submitting) return;
    setSubmitting(true);

    const corrdinatesArr = shapeCoords.map((coord, index) => ({
      index: index + 1,
      latitude: `${coord.lat}`,
      longitude: `${coord.lng}`,
    }));

    const payload = {
      nameAr: values.nameAr,
      nameEN: values.nameEn,
      govermentId: values.governmentId,
      isActive: values.isActive,
      isBusy: values.isBusy,
      boundaries: corrdinatesArr,
      countryId: values.countryId,
    };

    if (mode === "add") {
      addCity(
        payload,
        (res) => {
          setSubmitting(false);
          if (res.success) {
            toastSuccess(res.message);
            onSubmit();
          } else {
            toastError(res.message);
          }
        },
        (res) => {
          setSubmitting(false);
          toastError(res.message);
        },
      );
    } else {
      payload.id = rowData.id;
      if (values.governmentId === rowData.govenmentName) {
        payload.govermentId = rowData.govermentId;
      }
      editCity(
        payload,
        (res) => {
          setSubmitting(false);
          if (res.success) {
            toastSuccess(res.message);
            onSubmit();
          } else {
            toastError(res.message);
          }
        },
        (res) => {
          setSubmitting(false);
          toastError(res.message);
        },
      );
    }
  };

  useEffect(() => {
    if (mode === "edit" && rowData) {
      form.setFieldValue("nameAr", rowData.nameAr);
      form.setFieldValue("nameEn", rowData.nameEn);
      form.setFieldValue("isActive", rowData.isActive);
      form.setFieldValue("isBusy", rowData.isBusy);
      form.setFieldValue("countryId", rowData.countryId);
      const coords = rowData.boundaries.map((ele) => ({
        lat: +ele.latitude,
        lng: +ele.longitude,
      }));
      setShapeCoords(coords.length ? coords : null);
    }
  }, [rowData, mode, form]);

  useEffect(() => {
    if (mode === "add" || rowData) {
      getGovernmentsForList(
        form.getFieldValue("countryId") === 2 ? true : false,
        (res) => {
          if (res.success) {
            let isGovernmentDeactive = true;
            const optionsArr = res.data.map((ele) => {
              if (mode === "edit" && ele.id === rowData.govermentId) {
                isGovernmentDeactive = false;
              }
              return {
                label: ele.name,
                value: ele.id,
              };
            });
            if (mode === "edit") {
              if (isGovernmentDeactive) {
                form.setFieldValue("governmentId", rowData.govenmentName);
              } else {
                form.setFieldValue("governmentId", rowData.govermentId);
              }
            }
            setGovernmentsList(optionsArr);
          } else {
            toastError(res.message);
          }
        },
        (res) => {
          toastError(res?.data?.error);
        },
      );
    }
  }, [rowData, mode, form.getFieldValue("countryId")]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(
        (info) => {
          setSubmittable(true);
        },
        (info) => {
          if (info.errorFields.length === 0) {
            setSubmittable(true);
          } else {
            setSubmittable(false);
          }
        },
      );
  }, [values, form]);

  return (
    <Modal
      title={subHeader}
      open={open}
      className="msg-modal"
      footer={false}
      closable={false}
      centered
      destroyOnClose
      onCancel={onCancel}
    >
      <Form
        form={form}
        className="edit-actor__content pt-3"
        onFinish={handleOnFinish}
        initialValues={initialValues}
      >
        <Row gutter={24}>
          <Col span={24}>
            <label className="users__parent-info-label mb-2" htmlFor="">
              Country
            </label>
            <Form.Item
              name="countryId"
              rules={[{ required: true, message: "Please select a country." }]}
            >
              <Select
                placeholder="Select Country"
                className="w-100"
                style={{ height: "40px" }}
                options={countries.map((c) => ({ label: c.name, value: c.id }))}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <div className="new-actor__name new-category mb-5">
              <label className="users__parent-info-label mb-2" htmlFor="">
                Government
              </label>
              <Form.Item
                name="governmentId"
                className="w-100"
                rules={[
                  {
                    required: true,
                    message: "Please, Select the government",
                  },
                ]}
              >
                <Select
                  name="governmentId"
                  className="new-category__form-item"
                  placeholder="Select Government"
                  loading={governmentsList ? false : true}
                  options={governmentsList ? governmentsList : []}
                  allowClear
                  getPopupContainer={(trigger) => trigger.parentElement}
                />
              </Form.Item>
            </div>
          </Col>
          <Col span={24}>
            <div className="new-actor__name new-category mb-5">
              <label className="users__parent-info-label mb-2" htmlFor="">
                Area Name in English
              </label>
              <Form.Item
                className="new-category__form-item"
                name="nameEn"
                validateFirst
                rules={[
                  {
                    required: true,
                    whitepsace: true,
                    message: "Please, Enter the Area name in English",
                  },
                  {
                    pattern: /^\S+/,
                    message: "Please, Enter a valid name",
                  },
                  {
                    pattern: englishNameWithNoRestriction,
                    message: "Please, Enter the Area name in English",
                  },
                  {
                    pattern: englishLettersExist,
                    message: "Please, Enter a valid name",
                  },
                  {
                    pattern: /\S+$/,
                    message: "Please, Enter a valid name",
                  },
                  {
                    min: 3,
                    message: "Name should be at least 3 characters",
                  },
                  {
                    max: 50,
                    message: "Name should not exceed 50 characters.",
                  },
                ]}
              >
                <Input
                  type="text"
                  className="search__searchField py-2"
                  placeholder="Enter Name in English"
                />
              </Form.Item>
            </div>
          </Col>
          <Col span={24}>
            <div className="new-actor__name new-category mb-5">
              <label className="users__parent-info-label mb-2" htmlFor="">
                Area Name in Arabic
              </label>
              <Form.Item
                className=" new-category__form-item"
                name="nameAr"
                validateFirst
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Please, Enter the Area name in Arabic",
                  },
                  {
                    pattern: /^\S+/,
                    message: "Please, Enter a valid name",
                  },
                  {
                    pattern: arabicNameWithNoRestrictionValidation,
                    message: "Please, Enter the Area name in Arabic",
                  },
                  {
                    pattern: arabicLettersExist,
                    message: "Please, Enter a valid name",
                  },
                  {
                    pattern: /\S+$/,
                    message: "Please, Enter a valid name",
                  },
                  {
                    min: 3,
                    message: "Name should be at least 3 characters",
                  },
                  {
                    max: 50,
                    message: "Name should not exceed 50 characters.",
                  },
                ]}
              >
                <Input
                  type="text"
                  className="search__searchField py-2"
                  placeholder="Enter Name in Arabic"
                  dir="rtl"
                />
              </Form.Item>
            </div>
          </Col>
          <Col span={24}>
            <div className="new-actor__name mb-4 row mx-0 align-items-center">
              <div className="col-4 px-0 text-start">
                <label className="users__parent-info-label">Active</label>
              </div>
              <div className="col-8 px-0 d-flex">
                <Form.Item
                  name="isActive"
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Switch />
                </Form.Item>
              </div>
            </div>
          </Col>
          <Col span={24}>
            <div className="new-actor__name mb-4 row mx-0 align-items-center">
              <div className="col-4 px-0 text-start">
                <label className="users__parent-info-label">Is Busy</label>
              </div>
              <div className="col-8 px-0 d-flex">
                <Form.Item
                  name="isBusy"
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Switch />
                </Form.Item>
              </div>
            </div>
          </Col>
          <Col span={24}>
            <MapHolder
              mode={mode}
              coords={shapeCoords}
              rowData={rowData}
              onCoordsUpdate={handleCoordsUpdate}
            />
          </Col>
        </Row>
        <Row className="mrg-ys-20 mrg-x-auto mt-2 justify-content-center">
          <Space size={20}>
            <SecondaryButton buttonName={"Cancel"} onClick={onCancel} />
            <PrimaryButton
              buttonName={primaryBtnName}
              loading={submitting}
              type="submit"
              disableMe={submitting || !shapeCoords || !submittable}
            />
          </Space>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddEditModal;
